import api from './api';

export type FeedbackMood = 'negative' | 'neutral' | 'positive';

// How the development has handled a feedback, null while not handled
export type FeedbackStatus = 'not-done' | 'in-progress' | 'done';

// A feedback in the feedback list
export interface IFeedback {
  // The version the giver had (when the application was built, UTC), null when not known
  appBuiltAt: null | string;
  createdAt: string;
  // The department of the giver, null without one
  departmentName: null | string;
  id: number;
  // Exactly where, e.g. "AK1 Alakatto (/offers/products/12/offer)"
  location: null | string;
  mood: FeedbackMood;
  // When the giver closed or commented the notification of the response
  notificationClosedAt?: null | string;
  // When the giver was told of the response, null when not told
  notifiedAt?: null | string;
  // The page the averages are counted by, e.g. "Laskenta"
  page: string;
  // The feedback this one comments (the giver's answer to a response), null for its own
  previousFeedbackId?: null | number;
  rating: null | number;
  respondedAt: null | string;
  respondedUserName: null | string;
  // How the development has reacted
  response: null | string;
  status: FeedbackStatus | null;
  text: string;
  // The giver, null when the user has been removed
  userEmail: null | string;
  userName: null | string;
}

// The average of the overall ratings of a page
export interface IFeedbackAverage {
  average: number;
  count: number;
  page: string;
}

export interface IFeedbackList {
  // The average of all the overall ratings, null without ratings
  average: null | number;
  // The current user may answer the feedback
  canRespond: boolean;
  feedbacks: IFeedback[];
  giverCount: number;
  pages: IFeedbackAverage[];
  ratingCount: number;
  userCount: number;
  // The ISO weeks, the newest first
  weeks: {
    average: number;
    count: number;
    pages: IFeedbackAverage[];
    starts: string;
    week: number;
    year: number;
  }[];
}

export default {
  // The feedback of the signed-in user on a page
  post: (feedback: {
    // The version the user has, from the settings loaded with the page
    appBuiltAt: null | string;
    location: string;
    mood: FeedbackMood;
    page: string;
    rating: null | number;
    text: string;
  }): Promise<{ id: number }> => api.post('/api/v1/feedbacks', feedback),
  // The newest feedback and the averages, with the right to read the feedback
  get: (): Promise<IFeedbackList> => api.get('/api/v1/feedbacks'),
  // The answer of the development, with the right to handle the feedback
  respond: (
    id: number,
    response: { response: string; status: FeedbackStatus | null }
  ): Promise<void> => api.put(`/api/v1/feedbacks/${id}/response`, response),
  // Tells the giver of the response, with the right to handle the feedback
  notify: (id: number): Promise<{ notifiedAt: string }> =>
    api.post(`/api/v1/feedbacks/${id}/notify`, {}),
  // The responses to the signed-in user's feedback they have been told of and not closed
  getNotifications: (): Promise<IFeedback[]> =>
    api.get('/api/v1/feedbacks/notifications'),
  // The giver acknowledges a notification, which is recorded
  closeNotification: (id: number): Promise<{ closedAt: string }> =>
    api.post(`/api/v1/feedbacks/${id}/notification/close`, {}),
};
